import { useState, useMemo, useCallback } from "react";
import { Search, X, TrendingUp } from "lucide-react";
import { Modal }           from "@/components/ui/Modal";
import { MultiSelect }     from "@/components/ui/MultiSelect";
import {
  IncomeItem,
  BankStatementMovement,
  BankMovementFinanceiroLine,
  incomeReceiptLiquido,
} from "@/services/financeiro";
import { formatCurrency, formatDate } from "@/utils/format";
import { cn } from "@/utils/cn";
import { StatementTable } from "./FinanceiroModalContent";

/* ─── Props ──────────────────────────────────────────────────────────────── */

export interface ReceitasDetailModalProps {
  open:    boolean;
  onClose: () => void;
  title:   string;
  items:   IncomeItem[];
  /** Créditos do extrato SEMOG somados ao KPI de receitas */
  statementCredits?: BankStatementMovement[];
  /**
   * Total exibido no card «Recebimentos» (ex.: bulk bank-movement).
   * Quando definido e sem filtros na tabela, o rodapé mostra este valor para coincidir com o card.
   */
  totalIgualAoCardRecebimentos?: number;
  /**
   * Parcela «movimento/extrato» exatamente como no KPI (bulk bank-movement ou extrato SEMOG).
   * Quando definida, fecha a conta títulos + extrato sem depender só da soma das linhas do extrato SEMOG —
   * evita diferença "fantasma" quando as bases diferem.
   */
  extratoCreditoNoKpi?: number;
  /** Linhas bulk operação Recebimento (detalhe do movimento no card). */
  bankMovementRecebimentos?: BankMovementFinanceiroLine[];
  /** costCenterId → label; para exibição amigável (mesmo padrão do modal Pagamentos) */
  ccNameMap: Map<string, string>;
  /** paymentCategoryId → nome do plano financeiro */
  paymentCatMap: Map<string, string>;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const PER_PAGE = 25;

function matchFilter(value: string, filter: string): boolean {
  if (!filter) return true;
  return value.toLowerCase().includes(filter.toLowerCase());
}

function labelsFilterMatch(selected: string[], cellValue: string): boolean {
  if (selected.length === 0) return true;
  const v = cellValue.trim().toLowerCase();
  if (!v || v === "—") return false;
  return selected.some((s) => s.trim().toLowerCase() === v);
}

function ccLabel(item: IncomeItem, ccNameMap: Map<string, string>): string {
  return item.costCenterName
    || (item.costCenterId ? ccNameMap.get(item.costCenterId) ?? item.costCenterId : "")
    || "—";
}

function accountLabel(item: IncomeItem, paymentCatMap: Map<string, string>): string {
  if (item.accountId) {
    const fromMap = paymentCatMap.get(item.accountId);
    if (fromMap) return fromMap;
  }
  if (item.accountName) return item.accountName;
  if (item.accountId) return `Plano ${item.accountId}`;
  return "—";
}

/* ─── Filtros ────────────────────────────────────────────────────────────── */

interface Filters {
  titulo:     string;
  vencimento: string;
  pagamento:  string;
  cliente:    string;
  valor:      string;
  /** Rótulos de centro de custo selecionados (multiselect) — padrão Pagamentos. */
  centros:    string[];
  /** Rótulos de plano financeiro selecionados (multiselect) — padrão Pagamentos. */
  planos:     string[];
  documento:  string;
  status:     string;
}

const EMPTY_FILTERS: Filters = {
  titulo: "", vencimento: "", pagamento: "",
  cliente: "", valor: "", centros: [], planos: [], documento: "", status: "",
};

function hasActiveFilters(f: Filters): boolean {
  if (f.planos.length > 0 || f.centros.length > 0) return true;
  return (
    f.titulo.trim() !== "" ||
    f.vencimento.trim() !== "" ||
    f.pagamento.trim() !== "" ||
    f.cliente.trim() !== "" ||
    f.valor.trim() !== "" ||
    f.documento.trim() !== "" ||
    f.status.trim() !== ""
  );
}

/** Linha da grade: título (income) ou lançamento BC operação Recebimento. */
type GridRow =
  | { kind: "income"; item: IncomeItem }
  | { kind: "bankMove"; line: BankMovementFinanceiroLine };

function bankReceiveLineMatchesFilters(line: BankMovementFinanceiroLine, filters: Filters): boolean {
  const payBr = line.movementDate ? formatDate(line.movementDate) : "";
  const clienteHay = `${line.historico} ${line.operationName}`.trim();
  const valorStr = formatCurrency(line.amount);
  const ccStr = "Mov. bancário (BC)";
  const accStr = line.operationName || "—";
  const notesStr = line.historico || "—";
  const statusStr = "recebido";
  const tituloHay = `${line.id} ${line.historico}`.trim();

  return (
    (!filters.titulo.trim() || matchFilter(tituloHay, filters.titulo)) &&
    (!filters.vencimento.trim() ||
      matchFilter(line.movementDate, filters.vencimento) ||
      matchFilter(payBr, filters.vencimento)) &&
    (!filters.pagamento.trim() ||
      matchFilter(line.movementDate, filters.pagamento) ||
      matchFilter(payBr, filters.pagamento)) &&
    (!filters.cliente.trim() || matchFilter(clienteHay, filters.cliente)) &&
    (!filters.valor.trim() || matchFilter(valorStr, filters.valor)) &&
    labelsFilterMatch(filters.centros, ccStr) &&
    labelsFilterMatch(filters.planos, accStr) &&
    (!filters.documento.trim() || matchFilter(notesStr, filters.documento)) &&
    (!filters.status.trim() || matchFilter(statusStr, filters.status))
  );
}

function gridRowSortDateIncome(r: GridRow): string {
  if (r.kind === "income") {
    const d = r.item.paymentDate || r.item.dueDate || "";
    return d.slice(0, 10);
  }
  return (r.line.movementDate || "").slice(0, 10);
}

function incomeDocumentLabel(item: IncomeItem): string {
  return (item.documentIdentificationName || item.documentType || "").trim();
}

function receiptAuxCell(v: number | undefined) {
  const x = v ?? 0;
  if (Math.abs(x) < 0.0005) {
    return <span className="text-slate-400">—</span>;
  }
  return <span className="tabular-nums">{formatCurrency(x)}</span>;
}

/* ─── FilterInput ────────────────────────────────────────────────────────── */

function FilterInput({
  placeholder, value, onChange,
}: {
  placeholder: string;
  value:       string;
  onChange:    (v: string) => void;
}) {
  return (
    <div className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300 pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-6 pr-2 py-1 text-xs border border-slate-200 rounded-md bg-white text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
      />
    </div>
  );
}

/* ─── StatusBadge ────────────────────────────────────────────────────────── */

function StatusBadge({ paid }: { paid: boolean }) {
  return (
    <span className={cn(
      "inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap",
      paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    )}>
      {paid ? "Recebido" : "A Receber"}
    </span>
  );
}

/* ─── SubtotalBar ────────────────────────────────────────────────────────── */

function SubtotalBar({
  filtered, total, totalValue,
  footerValorPrincipal,
  footerRotulo,
}: {
  filtered:      number;
  total:         number;
  totalValue:    number;
  footerValorPrincipal: number;
  footerRotulo: string;
}) {
  const changed = filtered !== total;
  return (
    <div className={cn(
      "flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-3 rounded-xl text-sm border",
      changed
        ? "bg-brand-50 border-brand-200 text-brand-900"
        : "bg-slate-50 border-slate-200 text-slate-700"
    )}>
      <span className="font-medium text-xs">
        {changed
          ? `${filtered} de ${total} registro${total !== 1 ? "s" : ""} (filtrados)`
          : `${total} registro${total !== 1 ? "s" : ""}`}
      </span>
      <div className="flex flex-col items-end gap-0.5 text-right">
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <span className="text-xs font-medium opacity-70">{footerRotulo}:</span>
          <span className="font-bold text-base">{formatCurrency(footerValorPrincipal)}</span>
        </div>
        {changed && (
          <span className="text-xs opacity-60">
            Soma na grade (todos): {formatCurrency(totalValue)}
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Modal principal ────────────────────────────────────────────────────── */

export function ReceitasDetailModal({
  open,
  onClose,
  title,
  items,
  statementCredits,
  totalIgualAoCardRecebimentos,
  extratoCreditoNoKpi,
  bankMovementRecebimentos,
  ccNameMap,
  paymentCatMap,
}: ReceitasDetailModalProps) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage]       = useState(0);

  const set = useCallback((key: keyof Filters) => (v: string) => {
    setFilters((prev) => ({ ...prev, [key]: v }));
    setPage(0);
  }, []);

  const resetFilters = useCallback(() => { setFilters(EMPTY_FILTERS); setPage(0); }, []);

  const handleClose = useCallback(() => {
    onClose();
    setFilters(EMPTY_FILTERS);
    setPage(0);
  }, [onClose]);

  const bankLines = bankMovementRecebimentos ?? [];

  const centroOptions = useMemo(() => {
    const labels = new Set<string>();
    for (const item of items) {
      const lab = ccLabel(item, ccNameMap);
      if (lab && lab !== "—") labels.add(lab);
    }
    if (bankLines.length > 0) {
      labels.add("Mov. bancário (BC)");
    }
    return [...labels]
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((label) => ({ value: label, label }));
  }, [items, ccNameMap, bankLines]);

  const setCentros = useCallback((centros: string[]) => {
    setFilters((prev) => ({ ...prev, centros }));
    setPage(0);
  }, []);

  const planoOptions = useMemo(() => {
    const labels = new Set<string>();
    for (const item of items) {
      const lab = accountLabel(item, paymentCatMap);
      if (lab && lab !== "—") labels.add(lab);
    }
    for (const line of bankLines) {
      const lab = (line.operationName || "").trim();
      if (lab && lab !== "—") labels.add(lab);
    }
    return [...labels]
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((label) => ({ value: label, label }));
  }, [items, paymentCatMap, bankLines]);

  const setPlanos = useCallback((planos: string[]) => {
    setFilters((prev) => ({ ...prev, planos }));
    setPage(0);
  }, []);

  const filteredIncome = useMemo(() => {
    return items.filter((item) => {
      const valorBr   = formatCurrency(item.originalAmount);
      const liquidoBr = formatCurrency(incomeReceiptLiquido(item));
      const statusStr = item.isPaid ? "recebido" : "a receber";
      const docStr    = incomeDocumentLabel(item);
      const ccStr     = ccLabel(item, ccNameMap);
      const accStr    = accountLabel(item, paymentCatMap);

      return (
        matchFilter(item.billId,      filters.titulo)     &&
        matchFilter(item.dueDate,     filters.vencimento) &&
        matchFilter(item.paymentDate, filters.pagamento)  &&
        matchFilter(item.clientName,  filters.cliente)    &&
        (!filters.valor.trim() ||
          matchFilter(valorBr, filters.valor) ||
          matchFilter(liquidoBr, filters.valor))           &&
        labelsFilterMatch(filters.centros, ccStr) &&
        labelsFilterMatch(filters.planos, accStr) &&
        matchFilter(docStr,           filters.documento)  &&
        matchFilter(statusStr,        filters.status)
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filters, ccNameMap, paymentCatMap]);

  const filteredBankLines = useMemo(
    () => bankLines.filter((line) => bankReceiveLineMatchesFilters(line, filters)),
    [bankLines, filters],
  );

  const filteredCombined = useMemo(() => {
    const rows: GridRow[] = [
      ...filteredIncome.map((item) => ({ kind: "income" as const, item })),
      ...filteredBankLines.map((line) => ({ kind: "bankMove" as const, line })),
    ];
    rows.sort((a, b) => {
      const da = gridRowSortDateIncome(a);
      const db = gridRowSortDateIncome(b);
      const c = da.localeCompare(db);
      if (c !== 0) return c;
      if (a.kind !== b.kind) return a.kind === "income" ? -1 : 1;
      if (a.kind === "income" && b.kind === "income") {
        return `${a.item.billId}-${a.item.paymentDate}`.localeCompare(`${b.item.billId}-${b.item.paymentDate}`);
      }
      if (a.kind === "bankMove" && b.kind === "bankMove") {
        return a.line.id.localeCompare(b.line.id);
      }
      return 0;
    });
    return rows;
  }, [filteredIncome, filteredBankLines]);

  const incomeSumLiquido = useMemo(
    () => items.reduce((s, i) => s + incomeReceiptLiquido(i), 0),
    [items],
  );
  const bcSum = useMemo(() => bankLines.reduce((s, l) => s + l.amount, 0), [bankLines]);
  const totalRowCount = items.length + bankLines.length;
  const totalValueGrid = incomeSumLiquido + bcSum;

  const filteredCombinedSum = useMemo(
    () =>
      filteredCombined.reduce(
        (s, r) =>
          s + (r.kind === "income" ? incomeReceiptLiquido(r.item) : r.line.amount),
        0,
      ),
    [filteredCombined],
  );

  const extratoCredSumLista = useMemo(
    () => (statementCredits ?? []).reduce((s, x) => s + (x.credit > 0 ? x.credit : 0), 0),
    [statementCredits],
  );

  const extratoCredParaComposicaoCard =
    typeof extratoCreditoNoKpi === "number" && Number.isFinite(extratoCreditoNoKpi)
      ? extratoCreditoNoKpi
      : extratoCredSumLista;

  const activeFilters = hasActiveFilters(filters);

  const alinhaCard =
    typeof totalIgualAoCardRecebimentos === "number" &&
    Number.isFinite(totalIgualAoCardRecebimentos) &&
    !activeFilters;

  const detalheTitulosExtrato = incomeSumLiquido + extratoCredParaComposicaoCard;
  const gapMovimentoBancario =
    alinhaCard ? totalIgualAoCardRecebimentos! - detalheTitulosExtrato : 0;
  const mostrarGap = alinhaCard && Math.abs(gapMovimentoBancario) > 0.005;

  const pages    = Math.max(1, Math.ceil(filteredCombined.length / PER_PAGE));
  const safePage = Math.min(page, pages - 1);
  const visible  = filteredCombined.slice(safePage * PER_PAGE, (safePage + 1) * PER_PAGE);

  const footerValorPrincipal = activeFilters
    ? filteredCombinedSum
    : typeof totalIgualAoCardRecebimentos === "number" && Number.isFinite(totalIgualAoCardRecebimentos)
      ? totalIgualAoCardRecebimentos
      : incomeSumLiquido + extratoCredParaComposicaoCard;

  const footerRotulo = activeFilters
    ? "Subtotal (filtrado)"
    : typeof totalIgualAoCardRecebimentos === "number" && Number.isFinite(totalIgualAoCardRecebimentos)
      ? "Total (igual ao card)"
      : extratoCredParaComposicaoCard > 0
        ? "Total (títulos + extrato)"
        : "Subtotal";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </div>
          <span className="text-sm font-semibold text-slate-800 leading-tight">{title}</span>
        </div>
      }
      size="full"
    >
      <div className="flex flex-col gap-4 h-full">

        {/* ── Linha de filtros (padrão Pagamentos: MultiSelect CC + Plano) ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2 flex-shrink-0">
          <FilterInput placeholder="Nº Título"   value={filters.titulo}     onChange={set("titulo")}    />
          <FilterInput placeholder="Vencimento"  value={filters.vencimento} onChange={set("vencimento")}/>
          <FilterInput placeholder="Pagamento"   value={filters.pagamento}  onChange={set("pagamento")} />
          <FilterInput placeholder="Cliente"     value={filters.cliente}    onChange={set("cliente")}   />
          <FilterInput placeholder="Valor"       value={filters.valor}      onChange={set("valor")}     />
          <div className="min-w-0 [&_button]:!h-[26px] [&_button]:!min-h-[26px] [&_button]:!py-0 [&_button]:!text-xs [&_button]:!rounded-md [&_button]:!bg-white [&_button]:!border-slate-200">
            <MultiSelect
              value={filters.centros}
              onChange={setCentros}
              options={centroOptions}
              placeholder="Centro de Custo"
              maxDisplay={1}
              dropdownMinWidth={300}
              stopPropagation
            />
          </div>
          <div className="min-w-0 [&_button]:!h-[26px] [&_button]:!min-h-[26px] [&_button]:!py-0 [&_button]:!text-xs [&_button]:!rounded-md [&_button]:!bg-white [&_button]:!border-slate-200">
            <MultiSelect
              value={filters.planos}
              onChange={setPlanos}
              options={planoOptions}
              placeholder="Plano Financeiro"
              maxDisplay={1}
              dropdownMinWidth={300}
              stopPropagation
            />
          </div>
          <FilterInput placeholder="Documento"   value={filters.documento}  onChange={set("documento")} />
          <FilterInput placeholder="Status"      value={filters.status}     onChange={set("status")}    />
        </div>

        {activeFilters && (
          <div className="flex-shrink-0 flex items-center gap-3">
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              <X className="w-3 h-3" />
              Limpar filtros
            </button>
          </div>
        )}

        {statementCredits != null && statementCredits.length > 0 && (
          <div className="flex-shrink-0 rounded-xl border border-violet-100 bg-violet-50/50 p-4">
            <p className="text-xs font-semibold text-violet-900 uppercase tracking-wide mb-2">
              Créditos no extrato (contas SEMOG)
            </p>
            <StatementTable items={statementCredits} />
          </div>
        )}

        {/* ── Tabela ── */}
        <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-100">
          <table className="w-full text-xs border-collapse min-w-[1320px]">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b-2 border-slate-100">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Nº Título</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Vencimento</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Pagamento</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Cliente</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Valor</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Acréscimo</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Seguro</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Taxa adm</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Desconto</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3 whitespace-nowrap">Líquido</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Centro de Custo</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Plano Financeiro</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Documento</th>
                <th className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide py-2.5 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center py-12 text-slate-400 text-sm">
                    {items.length === 0 && bankLines.length === 0
                      ? "Nenhum título ou movimento bancário listado para este período."
                      : "Nenhum registro encontrado com os filtros aplicados."}
                  </td>
                </tr>
              ) : (
                visible.map((row, idx) => {
                  if (row.kind === "bankMove") {
                    const line = row.line;
                    const payBr = line.movementDate ? formatDate(line.movementDate) : "—";
                    const cliente =
                      line.historico && line.historico !== "—" ? line.historico : line.operationName;
                    return (
                      <tr
                        key={`bm-${line.id}-${idx}`}
                        className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors text-xs"
                      >
                        <td className="py-2.5 px-3 font-mono text-xs text-slate-500 whitespace-nowrap" title={line.id}>
                          —
                        </td>
                        <td className="py-2.5 px-3 text-slate-400 whitespace-nowrap text-xs">—</td>
                        <td className="py-2.5 px-3 whitespace-nowrap text-xs">
                          <span className="text-emerald-700 font-medium">{payBr}</span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-800 font-medium max-w-[180px] truncate text-xs" title={cliente}>
                          {cliente || "—"}
                        </td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                          {formatCurrency(line.amount)}
                        </td>
                        <td className="py-2.5 px-3 text-right text-xs text-slate-400">—</td>
                        <td className="py-2.5 px-3 text-right text-xs text-slate-400">—</td>
                        <td className="py-2.5 px-3 text-right text-xs text-slate-400">—</td>
                        <td className="py-2.5 px-3 text-right text-xs text-slate-400">—</td>
                        <td className="py-2.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums text-xs">
                          {formatCurrency(line.amount)}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 max-w-[140px] truncate text-xs">
                          Mov. bancário (BC)
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 max-w-[160px] truncate text-xs">
                          {line.operationName || "—"}
                        </td>
                        <td className="py-2.5 px-3 text-slate-500 min-w-[160px] max-w-[280px] text-xs whitespace-normal break-words leading-snug">
                          {line.historico && line.historico !== "—" ? line.historico : "—"}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <StatusBadge paid />
                        </td>
                      </tr>
                    );
                  }
                  const item = row.item;
                  const liquido = incomeReceiptLiquido(item);
                  const ccStr = ccLabel(item, ccNameMap);
                  const accStr = accountLabel(item, paymentCatMap);
                  return (
                    <tr
                      key={`${item.billId}-${item.costCenterId}-${item.accountId}-${idx}`}
                      className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors text-xs"
                    >
                      <td className="py-2.5 px-3 font-mono text-xs text-slate-600 whitespace-nowrap">
                        {item.billId || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-slate-700 whitespace-nowrap text-xs">
                        {item.dueDate ? formatDate(item.dueDate) : "—"}
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap text-xs">
                        {item.paymentDate ? (
                          <span className="text-emerald-700 font-medium">{formatDate(item.paymentDate)}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-slate-800 font-medium max-w-[180px] truncate text-xs">
                        {item.clientName || "—"}
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                        {formatCurrency(item.originalAmount)}
                      </td>
                      <td className="py-2.5 px-3 text-right whitespace-nowrap tabular-nums text-xs text-slate-800">
                        {receiptAuxCell(item.receiptAdditionAmount)}
                      </td>
                      <td className="py-2.5 px-3 text-right whitespace-nowrap tabular-nums text-xs text-slate-800">
                        {receiptAuxCell(item.receiptInsuranceAmount)}
                      </td>
                      <td className="py-2.5 px-3 text-right whitespace-nowrap tabular-nums text-xs text-slate-800">
                        {receiptAuxCell(item.receiptDueAdmAmount)}
                      </td>
                      <td className="py-2.5 px-3 text-right whitespace-nowrap tabular-nums text-xs text-slate-800">
                        {receiptAuxCell(item.receiptDiscountAmount)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums text-xs">
                        {formatCurrency(liquido)}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 min-w-[120px] max-w-[200px] text-xs whitespace-normal break-words leading-snug" title={ccStr}>
                        {ccStr}
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 min-w-[140px] max-w-[260px] text-xs whitespace-normal break-words leading-snug" title={accStr}>
                        {accStr}
                      </td>
                      <td className="py-2.5 px-3 text-slate-500 min-w-[160px] max-w-[280px] text-xs whitespace-normal break-words leading-snug">
                        {incomeDocumentLabel(item) || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <StatusBadge paid={item.isPaid} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Paginação + Subtotal ── */}
        <div className="flex-shrink-0 flex flex-col gap-2">
          <SubtotalBar
            filtered={filteredCombined.length}
            total={totalRowCount}
            totalValue={totalValueGrid}
            footerValorPrincipal={footerValorPrincipal}
            footerRotulo={footerRotulo}
          />
          {mostrarGap && (
            <p className="text-xs text-slate-500 px-1 leading-relaxed">
              Composição (títulos + base do extrato no card): {formatCurrency(detalheTitulosExtrato)} — o valor do card
              difere em {formatCurrency(Math.abs(gapMovimentoBancario))}
              {gapMovimentoBancario > 0 ? " a mais" : " a menos"}. Isso pode ocorrer se a lista de títulos não bate com o
              filtro do KPI ou se houver atualização parcial dos dados — use «Atualizar» ou revise o período aplicado.
            </p>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Página {safePage + 1} de {pages} · {filteredCombined.length} registro{filteredCombined.length !== 1 ? "s" : ""}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(0)} disabled={safePage === 0}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">«</button>
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">Anterior</button>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={safePage >= pages - 1}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">Próxima</button>
                <button onClick={() => setPage(pages - 1)} disabled={safePage >= pages - 1}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">»</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
